import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useEffect, useState } from "react";
import DamsTable from "../../components/Table/CollapsibleTable";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { ModalBase } from "../../components/Modals/ModalBase";
import ModalItemInput from "../../components/Input/ModalItemInput";
import { facilitiesApi } from "../../services/settings/facilities";
import { DistrictSelectInput } from "../../common-ui/select-input-models/DistrictSelectInput";
import { IFacility, CreateFacility, PageActions, IPagination, IQueryPerams } from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import { Field } from "../../utils/formSchema";
import { RegionSelectInput } from "../../common-ui/select-input-models/RegionSelectInput";
import ActionModal from "../../components/Modals/ActionModal";
import { usePagination } from "../../hooks/usePagination";
import { facilityTypeList } from "../../config/constants/faciltiesType";
import ModalItemSelectInput from "../../components/Input/ModalItemSelectInput";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { formatDate } from "../../utils/common";
import { FacilityLogsFilter } from "../../common-ui/page-filters/FacilityLogsFilter";
import { ExportModal } from "../../components/Modals/ExportModal";
import { appConfig } from "../../config/config";

function FacilitiesMainPage() {
  const [pageState, setPageState] = useState("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();

  interface FiltersState {
    search?: string;
    district_name?: string;
    facility_name?: string;
    is_deleted: boolean;
  }

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({
    is_deleted: false
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const {
    form,
    updateForm,
    updateInputField,
    setSubmitAttempt,
    onFormSubmit,
    fieldHasError
  } = useDmsForm<CreateFacility>({
    name: "",
    district_id: "",
    region_id: "",
    type: ""
  });

  const [isProcessing, setProcess] = useState(false);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState
  } = usePagination<IFacility[]>(facilitiesApi.getFacilities);
  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems
  } = useMangeTableItems(dataset);
  
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });   
  
  const populateDataSet = (data: IFacility[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      created_at: formatDate(data.created_at),
      updated_at: data.updated_at ? formatDate(data.updated_at) : 'Not Yet'
    })); 
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };
    
  const fetchData = async (query: IQueryPerams = {}) => {
    const result = await facilitiesApi.getFacilities({...query, is_deleted: false });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAddRequest() {
    setSubmitAttempt(true);
    const { isValid, data } = onFormSubmit();
    if (isValid) {
      setProcess(true);
      const request =
        pageAction?.type === "add"
          ? facilitiesApi.createFacility(data)
          : facilitiesApi.updateFacility(pageAction?.id ?? "", data);
      const res = await request;
      if (res.success) {
        fetchData(querySet);
      }
      setProcess(false);
      updatePageAction({
        type: pageAction?.type,
        message: res.message,
        title: pageAction?.type === "edit" ? "Update Facility" : "New Facility",
        success: res.success
      });
      openModel("alert");
    }
  }

  function addNewItem() {
    openModel("add");
    updatePageAction({
      type: "add"
    });
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: item.name
    });
  }

  function updateField(item: TRowItem) {
    const record = item as IFacility;
    updateForm({
      name: setValue(record.name),
      district_id: setValue(record.district_id),
      region_id: setValue(record.region_id),
      type: setValue(record.type)
    });
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.name,
      type: "edit",
      data: item
    });
  }

  async function handleDelete() {
    setProcess(true);
    const res = await facilitiesApi.deleteFacility(pageAction?.id ?? "");
    if (res.success) {
      fetchData(querySet);
    }
    setProcess(false);
    updatePageAction({
      title: res.success ? "Delete Facility" : "Delete Facility Failed",
      message: res.message,
      success: res.success
    });
    openModel("alert");
  }

  function toggleAlertModal() {
    if (pageAction?.type === "add") {
      openModel("add");
    } else {
      closeModel();
    }
  }

  function handleDistrictSelect(item: Field) {
    updateForm((p) => ({ ...p, district_id: item }));
  }

  function handleRegionSelect(item: Field) {
    updateForm((p) => ({ ...p, region_id: item }));
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await facilitiesApi.exportFacilities({
        file_format: 'csv',
        ...currentFilters,
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'facilities_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await facilitiesApi.exportFacilities({
        file_format: 'pdf',
        ...currentFilters,
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'facilities_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const renderHeaderDropDownMenu = ({
    closeDialog
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData(querySet);
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={facilitiesApi.deleteFacility}
          idKey={"id"}
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      title={"Facilities"}
      subtitle={""}
      pageState={pageState}
      addItemBtnOnClick={addNewItem}
      addItemBtnText="Add Facility"
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={""}
            subtitle={""}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <FacilityLogsFilter<IFacility[]>
              ftn={facilitiesApi.getFacilities}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />
        
            <DamsTable
              tableTitle="Facilities"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={8}
              onTableItemEdit={updateField}
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              headerDropDownMenu={renderHeaderDropDownMenu}
              tableTitleBadge={selectedRowItems.length===0?undefined:selectedRowItems.length + ' selected'}
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Data"
              subtitle="Choose your preferred format to export facilities data"
            />

            {/* Add facility Modal */}
            <ModalBase
              open={models.showAddModel}
              title={
                pageAction?.type === "add" ? "Add Facility" : "Edit Facility"
              }
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.Programmes}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt={
                pageAction?.type === "add" ? "Add Facility" : "Edit Facility"
              }
              onRightBtnPress={handleAddRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            >
              <Stack gap={2}>
                <RegionSelectInput
                  placeholder="Select region name"
                  label={
                    pageAction?.type !== "add" ? "Current Region" : "Region"
                  }
                  value={form.region_id.value}
                  handleChange={handleRegionSelect}
                  error={!form.region_id.isValid && form.submitAttempt}
                />
                <DistrictSelectInput
                  placeholder="Select district name"
                  label={
                    pageAction?.type !== "add" ? "Current District" : "District"
                  }
                  handleChange={handleDistrictSelect}
                  value={form.district_id.value}
                  error={!form.district_id.isValid && form.submitAttempt}
                />

                <ModalItemInput
                  label={
                    pageAction?.type !== "add" ? "Current Facility" : "Facility"
                  }
                  placeholder="Enter Facility Name"
                  handleChange={updateInputField("name")}
                  value={form.name.value}
                  error={fieldHasError("name")}
                  errorMessage={form.name.errorMessage}
                />
                <ModalItemSelectInput
                  items={facilityItems}
                  item=""
                  label={
                    pageAction?.type !== "add"
                      ? "Current Facility Type"
                      : "Facility Type"
                  }
                  placeholder="Enter select Facility type"
                  handleChange={updateInputField("type")}
                  value={form.type.value}
                  error={fieldHasError("type")}
                  errorMessage={form.type.errorMessage}
                />
              </Stack>
            </ModalBase>

            <ModalBase
              open={models.showDeleteModel}
              title={"Delete Facility"}
              subtitle={
                <Box>
                  Once you delete{" "}
                  <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>
                  , all related information will be lost forever. Are you sure
                  you want to proceed?
                </Box>
              }
              type={"action"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.delete_icon}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt="Delete"
              onRightBtnPress={handleDelete}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            />

            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

export default FacilitiesMainPage;

const facilityItems: {
  label: string;
  value: string;
}[] = facilityTypeList.map((item) => {
  return { label: item, value: item };
});
const tkeys = ["name", "district", "region", "created_at"];
const columns = ["Facility Name", "District", "Region", "Date Added"];
const setValue = (value: string) => ({ value, isValid: true });